
/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */
import { AlertTitle, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Tooltip, styled } from '@mui/material';

import {
    EventDefinitionsContext,
    Form,
    FormSubmitButton,
    IFormValidationError,
    // CrfCondition,
    // EventDefinitionsContext,
    // Field,
    // FieldGroup,
    // Form,
    // FormLookupsContext,
    // ICrfConditionParameters,
    // IFormLabel,
    // IFormSubmit,
    // IFormValidate,
    // IInstitution,
    IPatient,
    IPatientSummaryActionProps,
    PatientContext,
    PatientSummaryManager,
    // ValidationErrorType,
    // ValidationResultType,
    useSnackbar
} from '@ngt/opms';
// import { JsonServiceClient } from '@servicestack/client';
import { FC, useCallback, useContext, useMemo, useState } from 'react';

/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */
import { IFormSubmit, ValidateOn } from '@ngt/forms-core';
import { JsonServiceClient } from '@servicestack/client';
import ConsentFields, { consentFieldsLabels, consentFieldsLookups, useConsentFieldsValidation } from '../../../components/forms/fields/ConsentFields';
import { IInstitution, Patient, PatientStateType, SendConsentInformation, StartConsent } from '../../../api/dtos';


const StyledTooltip = styled(Tooltip)(() => ({
    display: 'flex'
}))

const StyledTooltipWrapper = styled(Box)(() => ({
    display: 'flex'
}))

const StyledEventButton = styled(Button)(({ theme }) => ({
    minWidth: 140,
    borderRadius: 0,
    whiteSpace: 'nowrap',
    '&:first-of-type': {
        marginLeft: theme.spacing(0)
    }
}))

const StyledEventButtonGroup = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2, 2, 2, 2),
    gap: theme.spacing(2),
    justifyContent: 'flex-end',
    display: 'flex'
}))

const StyledDialogContent = styled(DialogContent)(({theme}) => ({
    padding: theme.spacing(0, 0),
    overflow: 'visible',

    '&>p': {
        padding: `${theme.spacing(0, 2, 2, 2)} !important`,
    }
}));



enum PatientSummaryConsentDialogState {
    None = 0,
    Start = 1,
    Resend = 2,
    Restart = 3
}



const usePatientSummaryConsentActions = (
    updateTo: PatientStateType,
    hideButton: (patient: IPatient | null | undefined) => boolean,
    eventTitle: string,
    eventName: string,
    saving: boolean,
    updatePatientState: (state: PatientStateType, skipPatientEmail: boolean, patientStatusReason?: string) => void,
    enqueueSnackbar: ReturnType<typeof useSnackbar>['enqueueSnackbar'],
    client: JsonServiceClient,
    patient: IPatient | null | undefined,
    patientValid: boolean,
    institution: IInstitution | null | undefined,
    canUpdatePatient: boolean | undefined,
    manager: PatientSummaryManager | undefined
) => {
    const { data: eventDefinitions }  = useContext(EventDefinitionsContext);
    const { refresh: patientRefresh } = useContext(PatientContext);

    const eventDefinition = useMemo(() => eventDefinitions?.find(x => x.name === eventName), [eventDefinitions, eventName]);

    const [dialogState, setDialogState] = useState(PatientSummaryConsentDialogState.None);

    const openStartDialog = useCallback(() => {
        setDialogState(PatientSummaryConsentDialogState.Start);
    }, [setDialogState]);

    const openResendDialog = useCallback(() => {
        setDialogState(PatientSummaryConsentDialogState.Resend);
    }, [setDialogState]);

    const openRestartDialog = useCallback(() => {
        setDialogState(PatientSummaryConsentDialogState.Restart);
    }, [setDialogState]);

    const closeDialog = useCallback(() => {
        setDialogState(PatientSummaryConsentDialogState.None);
    }, [setDialogState]);

    const [sendingEmail, setSendingEmail] = useState(false);

    const onResendClick = useCallback(async () => {
        setSendingEmail(true);

        try {
            await client.post(new SendConsentInformation({ patientId: patient?.id }));

            enqueueSnackbar(
                <>
                    <AlertTitle>
                        {eventTitle} Notification Sent
                    </AlertTitle>
                    The {eventName} notification was successfully sent to the patient.
                </>,
                { variant: 'success' }
            );

            closeDialog();
            setSendingEmail(false);
        }
        catch {
            enqueueSnackbar(
                <>
                    <AlertTitle>
                        {eventTitle} Notification Not Sent
                    </AlertTitle>
                    An error occurred while attempting to create the {eventName} notification to the patient.
                </>,
                { variant: 'critical' }
            );

            closeDialog();
            setSendingEmail(false);
        }
    }, [patient, enqueueSnackbar, client, setSendingEmail, eventName, eventTitle, closeDialog]);

    const consentValidate = useConsentFieldsValidation(institution);

    const consentInitialValues = useMemo(() => {
        return {
            consentEvent: {
                patientId: patient?.id,
            },
            eventDefinitionId: eventDefinition?.id
        } as StartConsent

    }, [institution, patient, eventDefinition]);

    const [consentSaving, setConsentSaving] = useState(false); 

    const onConsentSubmit: IFormSubmit<StartConsent, IFormValidationError> = useCallback(async (form) => {
        closeDialog();
        setConsentSaving(true);

        try {
            await client.post(new StartConsent({...form.value}));

            await patientRefresh();

            if (manager) {
                await manager.load();
            }

            setConsentSaving(false);

            enqueueSnackbar(
                <>
                    <AlertTitle>
                        {eventTitle} Started
                    </AlertTitle>
                        {eventTitle} started successfully.
                </>,
                { variant: 'success' }
            );
        }
        catch (error: any) {
            
            setConsentSaving(false);

            enqueueSnackbar(
                <>
                    <AlertTitle>
                        Consent Not Started
                    </AlertTitle>
                    {error.responseStatus?.errorCode === 'ClientViewableException' ? error.responseStatus.message : 'An error occurred while attempting to start the consent.'}
                </>,
                { variant: 'critical' }
            );
        }
    
    }, [client, enqueueSnackbar, updatePatientState, closeDialog, setConsentSaving, patientRefresh, patient, eventDefinition, manager]);

    const action: FC<IPatientSummaryActionProps> = useCallback(({
        patient: actionPatient
    }) => {
        if (hideButton(actionPatient)) {
            return null;
        }

        if (actionPatient.patientStateId === updateTo) {
            return (
                <>
                    {
                        canUpdatePatient &&  (
                            <StyledEventButton
                                loading={saving || consentSaving}
                                variant="contained"
                                color="primary"
                                onClick={openRestartDialog}
                            >
                                Restart
                            </StyledEventButton>
                        )
                    }
                    <Dialog
                        open={dialogState === PatientSummaryConsentDialogState.Restart}
                        onClose={closeDialog}
                        aria-labelledby="restart-dialog-title"
                        aria-describedby="restart-dialog-description"
                        maxWidth="md"
                        fullWidth
                    >
                        <DialogTitle id="restart-dialog-title">Restart {eventTitle}</DialogTitle>
                        <StyledDialogContent>
                            <DialogContentText id="resend-dialog-description">
                                Restarting the {eventName} will remove any data in the existing {eventName} forms.
                            </DialogContentText>
                            <DialogContentText id="create-dialog-description">
                                Consent forms will be using the latest PICF version available by default.
                            </DialogContentText>

                            <Form
                                initialValue={consentInitialValues}
                                onSubmit={onConsentSubmit}    
                                lookups={consentFieldsLookups}
                                labels={consentFieldsLabels}
                                validateOn={ValidateOn.onChange}
                                onValidate={consentValidate}              
                            >
                                <ConsentFields institutionCode={(patient as Patient)?.institutionCode} />

                                <StyledEventButtonGroup
                                    display="flex"
                                    justifyContent="flex-end"
                                >
                                    <Button
                                        color="secondary"
                                        onClick={closeDialog}
                                        loading={consentSaving}  
                                    >
                                        Cancel
                                    </Button>

                                    <FormSubmitButton
                                        color="primary"   
                                        variant="contained"     
                                        loading={consentSaving}                                    
                                    >
                                        Restart
                                    </FormSubmitButton>

                                </StyledEventButtonGroup>
                            </Form>
                        </StyledDialogContent>
                    </Dialog>
                </>
            );
        }

        const startButton = (
            canUpdatePatient && (
                <StyledEventButton
                    loading={saving || consentSaving}
                    variant="contained"
                    color="primary"
                    onClick={openStartDialog}
                    disabled={!patientValid || saving || consentSaving}
                >
                    Start
                </StyledEventButton>
            )
        );

        const resendButton = (
            canUpdatePatient && (
                <StyledEventButton
                    loading={saving || consentSaving || sendingEmail}
                    variant="contained"
                    color="primary"
                    onClick={openResendDialog}
                    disabled={!patientValid || saving || consentSaving}
                >
                    Resend
                </StyledEventButton>
            )
        );

        if (patientValid) {
            return <>
                {startButton}
                {resendButton}
                <Dialog
                    open={dialogState === PatientSummaryConsentDialogState.Start}
                    onClose={closeDialog}
                    aria-labelledby="start-dialog-title"
                    aria-describedby="start-dialog-description"
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle id="start-dialog-title">Start {eventTitle}</DialogTitle>
                    <StyledDialogContent>
                        <DialogContentText id="create-dialog-description">
                            Please provide the following information to start the {eventName?.toLocaleLowerCase()}. 
                        </DialogContentText>
                        <DialogContentText id="create-dialog-description">
                            Consent forms will be using the latest PICF version available by default.
                        </DialogContentText>

                        <Form
                            initialValue={consentInitialValues}
                            onSubmit={onConsentSubmit}    
                            lookups={consentFieldsLookups}
                            labels={consentFieldsLabels}
                            validateOn={ValidateOn.onChange}  
                            onValidate={consentValidate}              
                        >
                            <ConsentFields institutionCode={(patient as Patient)?.institutionCode} />

                            <StyledEventButtonGroup
                                display="flex"
                                justifyContent="flex-end"
                            >
                                <Button
                                    color="secondary"
                                    onClick={closeDialog}
                                    loading={consentSaving}  
                                >
                                    Cancel
                                </Button>

                                <FormSubmitButton
                                    color="primary"   
                                    variant="contained"     
                                    loading={consentSaving}                                    
                                >
                                    Start
                                </FormSubmitButton>

                            </StyledEventButtonGroup>
                        </Form>
                    </StyledDialogContent>
                </Dialog>
                <Dialog
                    open={dialogState === PatientSummaryConsentDialogState.Resend}
                    onClose={closeDialog}
                    aria-labelledby="resend-dialog-title"
                    aria-describedby="resend-dialog-description"
                >
                    <DialogTitle id="resend-dialog-title">Resend {eventTitle} Information</DialogTitle>
                    <DialogContent>
                        <DialogContentText id="create-dialog-description">
                            Resending the {eventName} Information will send an email notification to the patient.
                            <br /><br />
                            Are you sure you wish to continue?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <StyledEventButtonGroup>
                            <Button onClick={closeDialog} color="secondary">
                                No
                            </Button>
                            <Button onClick={onResendClick} color="primary" variant="contained" autoFocus>
                                Yes
                            </Button>
                        </StyledEventButtonGroup>
                    </DialogActions>
                </Dialog>
            </>;
        }

        return (
            <StyledTooltip
                title="Complete the previous event to continue"
            >
                <StyledTooltipWrapper>
                    {startButton}
                    {resendButton}
                </StyledTooltipWrapper>
            </StyledTooltip>
        );
    }, [
        onResendClick,
        saving, 
        consentSaving,
        sendingEmail, 
        patientValid, 
        updateTo, 
        hideButton, 
        closeDialog, 
        dialogState, 
        eventName, 
        eventTitle, 
        openStartDialog, 
        openResendDialog, 
        openRestartDialog,
        onConsentSubmit,
        consentInitialValues,
        consentValidate,
        canUpdatePatient,
        manager
    ]);

    return action;
};

export default usePatientSummaryConsentActions;